import moment from 'moment';

function addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}

function formatDate(date) {
    return moment(date).format('YYYY-MM-DD');
}

function getWeekDates(cursor) {
    return Array.from({ length: 7 }, (_, i) => formatDate(addDays(cursor, i)));
}

function hasWorkShiftInWindow(weekDates, shiftData, fCodeIds) {
    return weekDates.some(d => {
        const entry = shiftData[d];
        return entry && entry.shiftId && !fCodeIds.has(entry.shiftId);
    });
}

function buildCycleWeeks(cycleStart, cycleEnd) {
    const weeks = [];
    let cursor = new Date(cycleStart);
    const end = new Date(cycleEnd);
    let weekNum = 1;
    while (cursor <= end) {
        weeks.push({ weekStart: formatDate(cursor), weekDates: getWeekDates(cursor), weekNum: weekNum++ });
        cursor = addDays(cursor, 7);
    }
    return weeks;
}

function buildCycleDays(cycleStart, cycleEnd) {
    const days = [];
    let cursor = new Date(cycleStart);
    const end = new Date(cycleEnd);
    while (cursor <= end) {
        days.push(formatDate(cursor));
        cursor = addDays(cursor, 1);
    }
    return days;
}

export function buildShiftDataFromCycleData(cycleData) {
    const shiftData = {};
    Object.values(cycleData).forEach(weekData => {
        Object.values(weekData).forEach(dayData => {
            if (!dayData || !dayData.date) return;
            // A day may carry a real shift, a pure F-code (no real shift), or both.
            // Fall back to fCodeId so pure F-code days still register as "occupied" for the algorithm.
            const primaryId = dayData.shiftCodeId || dayData.fCodeId;
            if (!primaryId) return;
            const date = moment(dayData.date, 'DD.MM.YYYY').format('YYYY-MM-DD');
            shiftData[date] = { shiftId: primaryId };
            if (dayData.shiftCodeId && dayData.fCodeId) shiftData[date].fCodeId = dayData.fCodeId;
        });
    });
    return shiftData;
}

export function mergeFCodeChangesIntoCycleData(cycleData, originalShiftData, processedShiftData, fCodeIds = new Set()) {
    const updated = {};
    for (const [week, weekData] of Object.entries(cycleData)) {
        updated[week] = { ...weekData };
    }

    for (const [date, entry] of Object.entries(processedShiftData)) {
        const m = moment(date);
        const weekNum = m.isoWeek();
        const dayKey = m.format('dddd').toLowerCase();
        // F-codes (F1/F2/F5) belong in fCodeId, never shiftCodeId — even when auto-placed.
        const field = fCodeIds.has(entry.shiftId) ? 'fCodeId' : 'shiftCodeId';

        if (!originalShiftData[date]) {
            // NEW — added by auto-placement (F1, F2, F5)
            if (updated[weekNum] && !updated[weekNum][dayKey]) {
                updated[weekNum][dayKey] = { [field]: entry.shiftId, date: m.format('DD.MM.YYYY') };
            }
        } else if (originalShiftData[date].shiftId !== entry.shiftId) {
            // CHANGED — same date, different shift (e.g. F2 displaced by F1 after pre-clear)
            if (updated[weekNum] && updated[weekNum][dayKey]) {
                const { shiftCodeId: _sc, fCodeId: _fc, ...rest } = updated[weekNum][dayKey];
                updated[weekNum][dayKey] = { ...rest, [field]: entry.shiftId };
            }
        }
    }

    for (const date of Object.keys(originalShiftData)) {
        if (!processedShiftData[date]) {
            const m = moment(date);
            const weekNum = m.isoWeek();
            const dayKey = m.format('dddd').toLowerCase();
            if (updated[weekNum] && updated[weekNum][dayKey]) {
                const orig = originalShiftData[date].shiftId;
                if (updated[weekNum][dayKey].shiftCodeId === orig || updated[weekNum][dayKey].fCodeId === orig) {
                    delete updated[weekNum][dayKey];
                }
            }
        }
    }

    return updated;
}

export function getClientNationalHolidays(clientHolidays, cycleStart, cycleEnd) {
    const allHolidays = (clientHolidays || []).flatMap(y => (y.data && y.data.holidays) || []);
    return allHolidays
        .filter(h =>
            h.isSelected &&
            Array.isArray(h.type) &&
            h.type.includes('National holiday') &&
            h.date >= cycleStart &&
            h.date <= cycleEnd
        )
        .map(h => h.date)
        .sort();
}

// F1 — Ukefridag (mandatory weekly rest)
function placeF1Week(weekDates, shiftData, f1ShiftId, fCodeIds) {
    weekDates.forEach(d => { if (shiftData[d]?.shiftId === f1ShiftId) delete shiftData[d]; });

    if (!hasWorkShiftInWindow(weekDates, shiftData, fCodeIds)) return;

    const offDays = weekDates.filter(d => !shiftData[d]?.shiftId);
    const sunday = offDays.find(d => new Date(d).getDay() === 0);
    const saturday = offDays.find(d => new Date(d).getDay() === 6);
    const target = sunday || saturday || offDays[0];
    if (target) shiftData[target] = { shiftId: f1ShiftId };
}

// Validates F1 for a single week. Returns a violation object or null.
function validateF1Week({ weekDates, weekStart, weekNum }, shiftData, f1ShiftId, fCodeIds, _t) {
    if (!hasWorkShiftInWindow(weekDates, shiftData, fCodeIds)) return null;
    if (weekDates.some(d => shiftData[d]?.shiftId === f1ShiftId)) return null;
    return {
        key: `week_${weekNum}_${weekStart}_f1_missing`,
        exception: `${_t('No F1 (statutory weekly rest) found in week')} ${weekNum} ${_t('starting')} ${weekStart}. ${_t('Required by Arbeidsmiljoloven SS 10-8(2).')}`,
        reason: '',
    };
}

// F2 — Ekstra ukefridag (extra weekly day off)
function placeF2(weeks, shiftData, f1ShiftId, f2ShiftId, f2Budget, fCodeIds) {
    let placed = 0;
    for (const { weekDates } of weeks) {
        if (placed >= f2Budget) break;

        // auto-place only adds one F2, and only into weeks that have none.
        if (weekDates.some(d => shiftData[d]?.shiftId === f2ShiftId)) continue;

        const hasWork = weekDates.some(d => shiftData[d]?.shiftId && !fCodeIds.has(shiftData[d].shiftId));
        const f1Date = weekDates.find(d => shiftData[d]?.shiftId === f1ShiftId);
        if (!hasWork || !f1Date) continue;

        const offDays = weekDates.filter(d => !shiftData[d]?.shiftId);
        if (!offDays.length) continue;

        const adjacent = offDays.find(d => Math.abs(new Date(d) - new Date(f1Date)) === 86400000);
        const target = adjacent || offDays[0];
        if (target) {
            shiftData[target] = { shiftId: f2ShiftId };
            placed++;
        }
    }
}

// F3 — Helgedagsfri (Sunday + public holiday rest)
function findNextF3Candidate(days, fromIdx, shiftData, holidaySet, f3ShiftId, usedDates) {
    for (let i = fromIdx + 1; i < days.length; i++) {
        const date = days[i];
        if (usedDates.has(date)) continue;
        const dow = new Date(date).getDay();
        if (dow !== 0 && !holidaySet.has(date)) continue;
        const entry = shiftData[date];
        if (!entry || !entry.shiftId || (f3ShiftId && entry.shiftId === f3ShiftId)) return date;
    }
    return null;
}

function validateF3(days, shiftData, holidaySet, f3ShiftId, fCodeIds, f4CompensatedDates, _t) {
    const violations = [];
    const usedDates = new Set();
    const legitimateF3Dates = new Set();

    for (let i = 0; i < days.length; i++) {
        const date = days[i];
        const dow = new Date(date).getDay();
        const isSunday = dow === 0;
        const isHoliday = holidaySet.has(date);

        if (!isSunday && !isHoliday) continue;
        if (isHoliday && f4CompensatedDates.has(date)) continue;

        const entry = shiftData[date];
        if (!entry || !entry.shiftId || fCodeIds.has(entry.shiftId)) continue;

        const suggestedDate = findNextF3Candidate(days, i, shiftData, holidaySet, f3ShiftId, usedDates);
        const alreadyPlaced = suggestedDate && shiftData[suggestedDate]?.shiftId === f3ShiftId;

        if (suggestedDate) {
            usedDates.add(suggestedDate);
            legitimateF3Dates.add(suggestedDate);
        }

        if (!alreadyPlaced) {
            const type = isHoliday ? 'public holiday' : 'Sunday';
            const suggestion = suggestedDate
                ? `${_t('Consider adding F3 (Helgedagsfri) on')} ${moment(suggestedDate).format('MMMM D')}.`
                : `${_t('No available Sunday or public holiday found in this cycle to place F3.')}`;

            violations.push({
                key: `${date}_f3_work_on_${isHoliday ? 'holiday' : 'sunday'}_date_to_add_f3_${suggestedDate || 'none'}`,
                exception: `${_t('Work shift on')} ${type} ${moment(date).format('MMMM D')}. ${suggestion}`,
                reason: '',
            });
        }
    }

    // Remove orphaned F3 placements
    for (const date of Object.keys(shiftData)) {
        if (shiftData[date]?.shiftId === f3ShiftId && !legitimateF3Dates.has(date)) {
            delete shiftData[date];
        }
    }

    return violations;
}

// F4 — Godtgjøringsfri (public holiday compensatory rest)
function findNextF4Candidate(days, fromIdx, shiftData, holidaySet, f4ShiftId, usedDates) {
    for (let i = fromIdx + 1; i < days.length; i++) {
        const date = days[i];
        const dow = new Date(date).getDay();
        if (dow < 1 || dow > 6 || holidaySet.has(date) || usedDates.has(date)) continue;
        const entry = shiftData[date];
        if (!entry || !entry.shiftId || (f4ShiftId && entry.shiftId === f4ShiftId)) return date;
    }
    return null;
}

function validateF4(days, shiftData, holidaySet, f4ShiftId, fCodeIds, _t) {
    const violations = [];
    const compensatedDates = new Set();
    const usedDates = new Set();

    for (let i = 0; i < days.length; i++) {
        const date = days[i];
        if (!holidaySet.has(date)) continue;

        const entry = shiftData[date];
        if (!entry || !entry.shiftId || fCodeIds.has(entry.shiftId)) continue;

        const suggestedDate = findNextF4Candidate(days, i, shiftData, holidaySet, f4ShiftId, usedDates);
        const alreadyPlaced = suggestedDate && shiftData[suggestedDate]?.shiftId === f4ShiftId;

        if (suggestedDate) usedDates.add(suggestedDate);

        if (alreadyPlaced) {
            compensatedDates.add(date);
        } else {
            const suggestion = suggestedDate
                ? `${_t('Consider adding F4 (Godtgjøringsfri) on')} ${moment(suggestedDate).format('MMMM D')}.`
                : `${_t('No available weekday found in this cycle to place F4.')}`;

            violations.push({
                key: `${date}_f4_work_on_holiday_date_to_add_f4_${suggestedDate || 'none'}`,
                exception: `${_t('Work shift on public holiday')} ${moment(date).format('MMMM D')}. ${suggestion}`,
                reason: '',
            });
        }
    }

    return { violations, compensatedDates };
}

// F5 — Erstatningsfridag (replacement rest day)
function placeF5Week(weekDates, sd, originalShiftData, days, holidaySet, f1ShiftId, f5ShiftId, approvedPlacements) {
    const f1Date = weekDates.find(d => {
        const entry = sd[d];
        if (!entry) return false;
        const isF1 = entry.shiftId === f1ShiftId || entry.fCodeId === f1ShiftId;
        if (!isF1) return false;
        const dow = new Date(d).getDay();
        return holidaySet.has(d) && dow >= 1 && dow <= 5;
    });
    if (!f1Date) return null;

    const f1Dow = new Date(f1Date).getDay(); // 1–5
    const windowStart = formatDate(addDays(new Date(f1Date), 8 - f1Dow));
    const windowEnd = formatDate(addDays(new Date(f1Date), 30));

    const isValidF5Day = (d) => {
        const dow = new Date(d).getDay();
        return d >= windowStart && d <= windowEnd && dow >= 1 && dow <= 5 && !holidaySet.has(d);
    };

    if (approvedPlacements.has(f1Date)) {
        const approved = approvedPlacements.get(f1Date);
        if (isValidF5Day(approved)) {
            sd[approved] = { shiftId: f5ShiftId };
            return { triggerDate: f1Date, placementDate: approved, windowStart, windowEnd };
        }
    }

    const secondaryDate = days.find(d => {
        if (!isValidF5Day(d)) return false;
        return sd[d]?.fCodeId === f5ShiftId;
    });
    if (secondaryDate) {
        return { triggerDate: f1Date, placementDate: secondaryDate, windowStart, windowEnd };
    }

    const previousDate = days.find(d => {
        if (!isValidF5Day(d)) return false;
        return originalShiftData[d]?.shiftId === f5ShiftId;
    });
    if (previousDate) {
        sd[previousDate] = { shiftId: f5ShiftId };
        return { triggerDate: f1Date, placementDate: previousDate, windowStart, windowEnd };
    }

    const autoDate = days.find(d => {
        if (!isValidF5Day(d)) return false;
        const entry = sd[d];
        return !entry || !entry.shiftId;
    });
    if (autoDate) {
        sd[autoDate] = { shiftId: f5ShiftId };
        return { triggerDate: f1Date, placementDate: autoDate, windowStart, windowEnd };
    }

    return { triggerDate: f1Date, placementDate: null, windowStart, windowEnd };
}

function validateF5(f5Results, _t) {
    const violations = [];

    for (const { triggerDate, windowEnd, placementDate } of f5Results) {
        if (placementDate) continue; // F5 was placed successfully

        violations.push({
            key: `${triggerDate}_f5_pending_for_f1_on_holiday_date_to_add_f5_none`,
            exception: `${_t('F5 (Erstatningsfridag) required')} — ${_t('F1 placed on national holiday')} ${moment(triggerDate).format('MMMM D')}. ${_t('No available weekday found in the 30-day for F5.')}`,
            reason: '',
        });
    }
    return violations;
}

// Main entry point
export function processFCodes(shiftData, cycleStart, cycleEnd, fCodeMap, fCodeIds, holidays, t, approvedF5Placements = new Map()) {
    const _t = t || (x => x);
    const sd = { ...shiftData };

    const doF1 = !!fCodeMap.F1;
    const doF2 = !!(fCodeMap.F1 && fCodeMap.F2);
    const doF5 = !!(fCodeMap.F1 && fCodeMap.F5);

    const weeks = buildCycleWeeks(cycleStart, cycleEnd);
    const days  = buildCycleDays(cycleStart, cycleEnd);
    const holidaySet = new Set(holidays);

    for (const date of Object.keys(sd)) {
        const id = sd[date]?.shiftId;
        if (!id) continue;
        // validation commented (when populating it will check and place the F2 once a week only)
        // if (doF2 && id === fCodeMap.F2) { delete sd[date]; continue; }
        if (doF5 && id === fCodeMap.F5) { delete sd[date]; continue; }
    }

    // ── Week pass 1: F1 place + F2 budget accumulate + F1 validate ──
    const f1Violations = [];
    let f2Budget = 0;

    for (const week of weeks) {
        const { weekDates } = week;

        if (doF1) placeF1Week(weekDates, sd, fCodeMap.F1, fCodeIds);

        if (doF2) {
            const workDays = weekDates.filter(d => sd[d]?.shiftId && !fCodeIds.has(sd[d].shiftId)).length;
            if (workDays > 0) f2Budget += Math.max(0, (7 - workDays) - 1);
        }

        if (doF1) {
            const violation = validateF1Week(week, sd, fCodeMap.F1, fCodeIds, _t);
            if (violation) f1Violations.push(violation);
        }
    }

    if (doF2) {
        placeF2(weeks, sd, fCodeMap.F1, fCodeMap.F2, f2Budget, fCodeIds);
    }

    const f5Results = [];
    if (doF5) {
        for (const { weekDates } of weeks) {
            const result = placeF5Week(weekDates, sd, shiftData, days, holidaySet, fCodeMap.F1, fCodeMap.F5, approvedF5Placements);
            if (result) f5Results.push(result);
        }
    }

    const violations = [...f1Violations];
    let f4CompensatedDates = new Set();
    if (fCodeMap.F4) {
        const { violations: f4v, compensatedDates } = validateF4(days, sd, holidaySet, fCodeMap.F4, fCodeIds, _t);
        violations.push(...f4v);
        f4CompensatedDates = compensatedDates;
    }

    if (fCodeMap.F3) {
        violations.push(...validateF3(days, sd, holidaySet, fCodeMap.F3, fCodeIds, f4CompensatedDates, _t));
    }

    if (doF5 && f5Results.length) {
        violations.push(...validateF5(f5Results, _t));
    }

    const f5Placements = f5Results;

    const blocking = violations.filter(v => v.key.includes('_f1_missing'));
    return { shiftData: sd, violations, canPublish: blocking.length === 0, f5Placements };
}
